import { reduce } from 'lodash'
import { useEffect } from 'react'

export const checkProductDependencyFulfilled = (dependentProducts = [], orderedProducts = {}, values = {}) =>
  dependentProducts.length < 1 ||
  dependentProducts.some(p => {
    const dProductId = p.primaryProduct
    if (!dProductId) {
      return true
    }

    const registrationProduct = values[dProductId]
    const isInOrder = registrationProduct ? registrationProduct.checked : false

    const orderedProduct = orderedProducts[dProductId]
    const isPreviouslyOrdered = !!orderedProduct
    const isPaid = orderedProduct ? !!orderedProduct?.isPaid : false

    if (p.mustBePaid) {
      return isPaid
    }

    return isInOrder || isPaid || isPreviouslyOrdered
  })

export const checkProductGroupMinMaxPurchase = (products, productGroups, values, groupMap, paidProducts) => {
  let newGroupMap = groupMap
  if (!groupMap) {
    newGroupMap = products.reduce((a, p) => {
      const pgId = p.relatedProductGroup
      if (!pgId) {
        return a
      }
      const depFulfilled = checkProductDependencyFulfilled(p.dependentProducts, paidProducts, values)
      if (!depFulfilled) {
        return a
      }
      if (a[pgId]) {
        a[pgId].push(p)
        return a
      }
      a[pgId] = [p]
      return a
    }, {})
  }
  // biome-ignore lint/performance/noAccumulatingSpread: TODO
  const productMap = products.reduce((a, p) => ({ ...a, [p.sfid]: p }), {})

  const selectedProductsInGroup = {}
  const paidMandatoryProductsInGroup = {}
  const paidProductsInGroup = {}

  Object.entries(values).forEach(([key, { checked }]) => {
    const { relatedProductGroup: pgId, isMandatory, allowMultiplePurchase } = productMap[key] || {}
    const orderedProduct = paidProducts[key]
    const isPaid = !!orderedProduct?.isPaid
    const isMandatoryPaid = isMandatory && !!orderedProduct?.isPaid

    if (!selectedProductsInGroup[pgId]) {
      selectedProductsInGroup[pgId] = 0
    }

    if (!paidMandatoryProductsInGroup[pgId]) {
      paidMandatoryProductsInGroup[pgId] = 0
    }

    if (checked && !isMandatoryPaid) {
      selectedProductsInGroup[pgId] += 1
    }

    if (!paidProductsInGroup[pgId]) {
      paidProductsInGroup[pgId] = 0
    }

    if (isPaid && !allowMultiplePurchase) {
      paidProductsInGroup[pgId] += 1
    }

    if (isMandatoryPaid) {
      paidMandatoryProductsInGroup[pgId] += 1
    }
  })

  const fulfilledMap = productGroups.reduce((a, pg) => {
    const minimumProductCount = Number(pg.minimumProductCount || 0)
    const maximumProductCount = Number(pg.maximumProductCount || 0)

    if (minimumProductCount === 0 && maximumProductCount === 0) {
      a[pg.id] = true
      return a
    }

    if ((newGroupMap[pg.id] || []).length === 0) {
      a[pg.id] = true
      return a
    }

    if ((newGroupMap[pg.id] || []).length === paidProductsInGroup[pg.id]) {
      a[pg.id] = true
      return a
    }

    const selectedCount = selectedProductsInGroup[pg.id] || 0
    const paidMandatoryCount = paidMandatoryProductsInGroup[pg.id] || 0
    const selectedAndPaidCount = selectedCount + paidProductsInGroup[pg.id]

    let isMinimumFulfilled = minimumProductCount === 0
    let isMaximumFulfilled = maximumProductCount === 0

    if (minimumProductCount > 0) {
      isMinimumFulfilled = selectedAndPaidCount >= minimumProductCount || paidMandatoryCount >= minimumProductCount
    }

    if (maximumProductCount > 0) {
      // Products already paid for cannot be given back, so only new selections can breach the maximum.
      const remainingAllowance = Math.max(maximumProductCount - (paidProductsInGroup[pg.id] || 0), 0)
      isMaximumFulfilled = selectedCount <= remainingAllowance
    }

    a[pg.id] = isMinimumFulfilled && isMaximumFulfilled

    return a
  }, {})

  return {
    fulfilledMap,
    selectedProductsInGroup,
    paidProductsInGroup,
  }
}

export const depFilter = (products, paidProducts, values) =>
  products.reduce((a, p) => {
    if (checkProductDependencyFulfilled(p.dependentProducts, paidProducts, values)) {
      a.push(p)
    }
    return a
  }, [])

export function FormikEffectComp({
  setContinueDisabled,
  formik,
  preselectedProduct,
  setShowUnpurchasable,
  hasPreselectedProduct,
  paidProducts,
  setMinimumFulfilled,
  setMaximumFulfilled,
  productGroups,
  products,
  groupMap,
  setDisplayGroupMap,
  hasSubEvents,
  allowSkip,
  parentValues,
  setPgSelectedPlusPaid,
}) {
  const { values } = formik
  let allValues = values
  if (parentValues) {
    allValues = { ...values, ...parentValues }
  }
  // unpurchasable effect
  useEffect(() => {
    if (!hasPreselectedProduct) {
      return setShowUnpurchasable(false)
    }
    if (!preselectedProduct) {
      return setShowUnpurchasable(true)
    }
    const depFulfilled = checkProductDependencyFulfilled(preselectedProduct.dependentProducts, paidProducts, allValues)
    setShowUnpurchasable(!depFulfilled)
  }, [values])
  // product group minimum purchase effect
  useEffect(() => {
    // generate new product group map
    const newGroupMap = reduce(
      groupMap,
      (a, pg, key) => {
        const newPg = pg.filter(pp => checkProductDependencyFulfilled(pp.dependentProducts, paidProducts, allValues))
        a[key] = newPg
        return a
      },
      {}
    )
    setDisplayGroupMap(newGroupMap)
    // check product group minimum and maximum purchase fulfilled
    const { fulfilledMap, selectedProductsInGroup, paidProductsInGroup } = checkProductGroupMinMaxPurchase(
      products,
      productGroups,
      allValues,
      newGroupMap,
      paidProducts
    )
    setMinimumFulfilled(fulfilledMap)
    setMaximumFulfilled(fulfilledMap)

    const pgPaidAndSelectedObjs = [
      ...new Set([...Object.keys(selectedProductsInGroup), ...Object.keys(paidProductsInGroup)]),
    ]

    if (typeof setPgSelectedPlusPaid === 'function') {
      setPgSelectedPlusPaid(
        pgPaidAndSelectedObjs.reduce((result, key) => {
          if (key !== 'undefined') {
            result[key] = (selectedProductsInGroup[key] || 0) + (paidProductsInGroup[key] || 0)
          }
          return result
        }, {})
      )
    }

    const continueDisabled = !allowSkip
      ? hasSubEvents
        ? Object.values(fulfilledMap).some(v => !v)
        : !Object.values(values || {}).some(v => v.checked) || Object.values(fulfilledMap).some(v => !v)
      : false

    setContinueDisabled(continueDisabled)
  }, [values])

  return null
}

export const unselectedProduct = {
  quantity: undefined,
  checked: false,
  formProductId: undefined,
}

export const noRegoMsg =
  'Unfortunately there are no available products that match your registration details. Please check your details and try again.'

export const noSubEventsMsg =
  'Unfortuantely there are no available products that match your registration details. Please check your details if you think this is incorrect, otherwise you can continue through to payment.'

export const noAddOnMessage = 'There are no Add-on Products available at this time.'

export const noShopMessage = 'There are no Add-ons available at this time.'

const combinationKey = optionIds =>
  [...optionIds]
    .map(Number)
    .sort((a, b) => a - b)
    .join('-')

const _optionsLeft = (optionIds, productAttributes = []) => {
  const lefts = productAttributes
    .flatMap(group => group.items || [])
    .filter(item => optionIds.some(optionId => Number(optionId) === Number(item.id)))
    .map(item => item.remainingProductQuantity)
    .filter(left => left != null)
  return lefts.length ? Math.min(...lefts) : null
}

const _smallest = lefts => {
  const known = lefts.filter(left => left != null)
  return known.length ? Math.min(...known) : null
}

export const combinationLeft = (optionIds, combinations = [], productLeft = null, productAttributes = []) => {
  const key = combinationKey(optionIds)
  const found = combinations.find(combination => combinationKey(combination.optionIds) === key)
  return _smallest([
    found ? found.remainingProductQuantity : null,
    productLeft,
    _optionsLeft(optionIds, productAttributes),
  ])
}

export const optionAvailable = ({
  productAttributes,
  combinations,
  productLeft,
  chosen = [],
  groupIndex,
  optionId,
  minimum = 1,
}) => {
  const pools = productAttributes.map((group, index) => {
    if (index === groupIndex) return [optionId]
    if (chosen[index] != null) return [chosen[index]]
    return group.items.map(item => item.id)
  })
  const completions = pools.reduce((all, pool) => all.flatMap(picked => pool.map(id => [...picked, id])), [[]])
  return completions.some(optionIds => {
    const left = combinationLeft(optionIds, combinations, productLeft, productAttributes)
    return left == null || left >= minimum
  })
}
